/**
 * YAML → XML Tool
 *
 * Converts YAML to XML by parsing to a JS object and serializing with fast-xml-parser.
 */

import { XMLBuilder } from 'fast-xml-parser';
import { parse as parseYaml } from 'yaml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const YamlToXmlTool: ITransformerTool = {
  id: 'yaml-to-xml',
  name: 'YAML → XML',
  description: 'Convert YAML to XML',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['yaml', 'xml', 'convert', 'transform'],

  seo: {
    slug: 'yaml-to-xml',
    pageTitle: 'YAML to XML Converter - Free Online Tool | Prism',
    metaDescription: 'Convert YAML to XML format instantly. Supports nested structures, arrays, and custom root elements. Fast, private, runs entirely in your browser.',
    keywords: ['yaml to xml', 'yaml to xml converter', 'convert yaml to xml online', 'yaml xml transformer'],
    relatedTools: ['xml-to-yaml', 'yaml-to-json', 'json-to-xml'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.YAML],
  outputType: SEMANTIC_TYPES.XML,

  attributes: [
    {
      name: 'rootName',
      type: 'string',
      label: 'Root Element',
      defaultValue: 'root',
      description: 'Name of the root XML element',
    },
    {
      name: 'pretty',
      type: 'boolean',
      label: 'Pretty Print',
      defaultValue: true,
      description: 'Indent and format the XML output',
    },
    {
      name: 'indentBy',
      type: 'select',
      label: 'Indent',
      defaultValue: '  ',
      options: ['  ', '    ', '\t'],
      description: 'Indentation string for pretty output',
    },
  ],

  supportsInput(_input: string, detectedType?: string): boolean {
    return detectedType === 'YAML';
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const rootName = String(config.rootName ?? 'root').trim() || 'root';
    const pretty = config.pretty !== false;
    const indentBy = (config.indentBy as string) || '  ';

    let parsed: unknown;
    try {
      parsed = parseYaml(input);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Invalid YAML';
      throw ToolError.validation(`Invalid YAML: ${msg}`, {
        suggestion: 'Check indentation and ensure mappings use "key: value" syntax',
      });
    }

    try {
      const builder = new XMLBuilder({
        ignoreAttributes: false,
        format: pretty,
        indentBy,
        suppressEmptyNode: true,
      });

      const wrapped = { [rootName]: parsed } as Record<string, unknown>;
      return builder.build(wrapped);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to build XML';
      throw ToolError.execution(msg);
    }
  },
};
